import { motion, AnimatePresence } from "framer-motion";
import { useState, useEffect } from "react";
import {DocumentRepository2} from '../../components/DocumentRepository';
import VisorSection from '../../sections/VisorSection';
import FasesSection from '../../sections/FasesSection';
import Descargas from '../../components/Descargas';
import './RioZula.css';
import {
  Home as HomeIcon,
  MapPin as MapPinIcon,
  FileText as FileTextIcon,
  Phone as PhoneIcon,
  ChevronDown,
  ChevronRight,
  Menu,
  X,
  ExternalLink,
  Users,
  Map,
  Landmark,
  Calendar,
  MapPin,
  Info,
  MountainSnow,
  Trees,
  FileSignature,
  FileSpreadsheet,
  BarChart3,
  Zap,
  FileText,
  ClockIcon,
  TreePineIcon,
  Book,
  BookOpen,
  FileSearch,
  Layers,
  Target,
  GlobeIcon,
  Compass,
  DownloadCloud,
  CheckCircle,
  ArrowDown,
  CheckIcon,
  FilePdf,
  ProjectDiagram,
  Lightbulb,
  ClipboardList,
} from 'lucide-react';

const DetailBadge = ({ icon: Icon, label, value }) => (
  <div className="flex items-center p-3 bg-gray-50 dark:bg-gray-700 rounded-xl transition-all hover:bg-teal-100 dark:hover:bg-teal-900/30">
    <Icon className="text-teal-600 dark:text-teal-400 mr-3 flex-shrink-0" size={20} />
    <div className="text-sm">
      <span className="font-semibold text-gray-700 dark:text-gray-300 block leading-tight">{label}:</span>
      <span className="text-gray-900 dark:text-white font-bold">{value}</span>
    </div>
  </div>
);

const ModernBitacoraSection = ({ items }) => {
  const [expandedSection, setExpandedSection] = useState(0);

  return (
    <section className="py-16 bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
            <span className="bg-gradient-to-r from-blue-600 to-teal-600 bg-clip-text text-transparent">
              Bitácora del Programa
            </span>
          </h2>
          <p className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            Registro documental completo del proceso de ordenamiento ecológico
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {items.map((item, index) => {
            const Icon = item.icon;
            return (
              <motion.button
                key={index}
                onClick={() => setExpandedSection(expandedSection === index ? null : index)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className={`p-6 rounded-2xl transition-all duration-300 text-left ${
                  expandedSection === index
                    ? `bg-gradient-to-br ${item.color} text-white shadow-2xl`
                    : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 shadow-lg hover:shadow-xl'
                }`}
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className={`p-3 rounded-xl ${
                    expandedSection === index
                      ? 'bg-white/20'
                      : `bg-gradient-to-br ${item.color}`
                  }`}>
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className="text-lg font-semibold">{item.title}</h3>
                </div>
                <p className={`text-sm ${
                  expandedSection === index ? 'text-white/90' : 'text-gray-500 dark:text-gray-400'
                }`}>
                  {expandedSection === index ? 'Ver documentos ↓' : 'Ver documentos →'}
                </p>
              </motion.button>
            );
          })}
        </div>

        <AnimatePresence>
          {expandedSection !== null && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="overflow-hidden"
            >
              <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-xl border border-gray-100 dark:border-gray-700">
                {items[expandedSection].content.map((section, sectionIndex) => (
                  <div key={sectionIndex} className="mb-8 last:mb-0">
                    <h4 className="text-xl font-semibold text-gray-900 dark:text-white mb-6 flex items-center">
                      <div className="w-3 h-3 bg-gradient-to-r from-blue-500 to-teal-500 rounded-full mr-3" />
                      {section.subtitle}
                    </h4>
                    <div className="grid md:grid-cols-2 gap-4">
                      {section.items.map((doc, docIndex) => (
                        <a
                          key={docIndex}
                          href={doc.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="group p-4 bg-gray-50 dark:bg-gray-700 rounded-xl hover:bg-gradient-to-r hover:from-blue-50 hover:to-teal-50 
                            dark:hover:from-blue-900/20 dark:hover:to-teal-900/20 transition-all duration-300"
                        >
                          <div className="flex items-center gap-3">
                            <FileText className="w-5 h-5 text-blue-500 group-hover:text-teal-500 transition-colors" />
                            <span className="text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-white">
                              {doc.text}
                            </span>
                          </div>
                        </a>
                      ))}
                    </div>
                  </div>
                ))}
                <div className="mt-8 pt-6 border-t border-gray-100 dark:border-gray-700">
                  <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
                    {items[expandedSection].content.reduce((total, section) => total + section.items.length, 0)} documentos disponibles
                  </p>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};

const FullWidthVisorWithSidebar = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeTab, setActiveTab] = useState('uga');

  return (
    <section id="visor-completo" className="py-16">
      <div className="max-w-[2000px] mx-auto px-4">
        <div className="text-center mb-10">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
            <span className="bg-gradient-to-r from-blue-600 to-teal-600 bg-clip-text text-transparent">
              Plataforma Geoespacial Completa
            </span>
          </h2>
          <p className="text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
            Sistema de información geográfica integrado con documentación técnica completa
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-6">
          <motion.div
            initial={{ x: -20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            className={`lg:w-96 ${sidebarOpen ? 'block' : 'hidden lg:block'}`}
          >
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-xl border border-gray-100 dark:border-gray-700 sticky top-6">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="lg:hidden mb-4 w-full p-2 bg-gradient-to-r from-blue-500 to-teal-500 
                  text-white rounded-lg flex items-center justify-center gap-2"
              >
                {sidebarOpen ? 'Ocultar Panel' : 'Mostrar Panel'}
                <ChevronRight className={`w-4 h-4 transition-transform ${sidebarOpen ? 'rotate-90' : ''}`} />
              </button>

              <div className="flex mb-6 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                <button
                  onClick={() => setActiveTab('uga')}
                  className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-all ${
                    activeTab === 'uga'
                      ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  Clasificación UGA
                </button>
                <button
                  onClick={() => setActiveTab('descripcion')}
                  className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-all ${
                    activeTab === 'descripcion'
                      ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  Descripción UGA
                </button>
                <button
                  onClick={() => setActiveTab('politica')}
                  className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-all ${
                    activeTab === 'politica'
                      ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  Política Ambiental
                </button>
              </div>

              <AnimatePresence mode="wait">
                <motion.div
                  key={activeTab}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                  className="space-y-6"
                >
                  {activeTab === 'uga' && (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center">
                        <Layers className="w-5 h-5 text-blue-500 mr-2" />
                        Clasificación de UGAs
                      </h3>
                      <img
                        src="/images/clave_uga_rio_zula.png"
                        alt="Clasificación de Unidades de Gestión Ambiental"
                        className="w-full h-auto rounded-lg shadow-md mb-4"
                      />
                      <p className="text-sm text-gray-600 dark:text-gray-300">
                        Mapa de clasificación de las Unidades de Gestión Ambiental (UGAs) 
                        de la región Río Zula.
                      </p>
                    </div>
                  )}

                  {activeTab === 'descripcion' && (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center">
                        <Info className="w-5 h-5 text-teal-500 mr-2" />
                        Descripción de las UGAs
                      </h3>
                      <p className="text-gray-600 dark:text-gray-300 mb-4">
                        Las <strong>Unidades de Gestión Ambiental (UGAs)</strong> son la unidad mínima 
                        del territorio a la que se asignan determinados lineamientos y estrategias 
                        ecológicas para su manejo específico.
                      </p>
                      <div className="space-y-4">
                        <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-lg p-4">
                          <h4 className="font-medium text-gray-900 dark:text-white mb-2">Características</h4>
                          <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-1">
                            <li>• Unidad territorial homogénea</li>
                            <li>• Criterios específicos de manejo</li>
                            <li>• Estrategias ecológicas definidas</li>
                            <li>• Monitoreo individualizado</li>
                          </ul>
                        </div>
                        <div className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 rounded-lg p-4">
                          <h4 className="font-medium text-gray-900 dark:text-white mb-2">Objetivos</h4>
                          <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-1">
                            <li>• Gestión ambiental específica</li>
                            <li>• Aplicación de lineamientos</li>
                            <li>• Evaluación de resultados</li>
                            <li>• Adaptación de estrategias</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}

                  {activeTab === 'politica' && (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center">
                        <Target className="w-5 h-5 text-purple-500 mr-2" />
                        Política Ambiental
                      </h3>
                      <p className="text-gray-600 dark:text-gray-300 mb-4">
                        El presente <strong>Modelo de Ordenamiento Ecológico y Territorial Regional</strong> considera una zonificación, integrada por seis políticas que buscan definir el estado deseable del territorio mediante lineamientos, estrategias y criterios para conservar y controlar el deterioro del ambiente así como el aprovechamiento sustentable de los recursos naturales.
                      </p>
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>

              <div className="mt-8 pt-6 border-t border-gray-100 dark:border-gray-700">
                <div className="grid grid-cols-2 gap-3">
                  <a
                    href="https://siga.jalisco.gob.mx/ordenamiento/visores/visorRioZula/index.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-2.5 bg-gradient-to-r from-blue-500 to-teal-500 text-white 
                      rounded-lg text-sm font-medium text-center hover:shadow-lg transition-all"
                  >
                    <ExternalLink className="w-4 h-4 inline mr-1" />
                    Abrir en Nueva Ventana
                  </a>
                </div>
              </div>
            </div>
          </motion.div>

          <div className="flex-1">
            <div className="bg-gray-900 rounded-2xl overflow-hidden shadow-2xl">
              <div className="p-4 bg-gradient-to-r from-gray-800 to-gray-900 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-gradient-to-r from-blue-500 to-teal-500 rounded-lg">
                    <GlobeIcon className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white">Visor GIS Río Zula</h3>
                    <p className="text-sm text-gray-400">Sistema interactivo de información geográfica</p>
                  </div>
                </div>
              </div>
              <div className="h-[600px]">
                <iframe
                  src="https://siga.jalisco.gob.mx/ordenamiento/visores/visorRioZula/index.html"
                  className="w-full h-full border-0"
                  title="Visor GIS Río Zula"
                  allowFullScreen
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const StatusBadge = () => {
  return (
    <motion.div 
      initial={{ scale: 0.9, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      className="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-emerald-500/20 to-teal-500/20 
        backdrop-blur-md border border-emerald-500/30"
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
    >
      <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse mr-2" />
      <span className="text-sm font-medium text-white tracking-wider">FASE </span>
      <div className="ml-2 pl-2 border-l border-white/20">
        <span className="text-xs text-white/70">En Ejecución</span>
      </div>
    </motion.div>
  );
};

const scrollToSection = (sectionId) => {
  const element = document.getElementById(sectionId);
  if (element) {
    const offset = 80;
    const elementPosition = element.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - offset;
    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth'
    });
  }
};

const Items = [
  {
    title: "Archivos Vectoriales",
    description: "Archivos geoespaciales para la consulta SIG.",
    imageUrl: "images/vectoriales.png",
    baseColor: "sky",
    link: "https://siga.jalisco.gob.mx/ordenamiento/archivos/Zula/Descargas/VectorialesZula.zip",
    target: "_blank",
  },
  {
    title: "Fichas UGAs",
    description: "Informacion detallada de cada Unidad de Gestión Ambiental (UGA).",
    imageUrl: "images/FICHAS_UGA.png",
    baseColor: "green",
    link: "https://siga.jalisco.gob.mx/ordenamiento/archivos/Zula/Descargas/Fichas%20de%20las%20Unidades%20de%20Gesti%C3%B3n%20Ambiental%20Regi%C3%B3n%20Cuenca%20R%C3%ADo%20Zula.pdf",
    target: "_blank",
  },
  {
    title: "Criterios",
    description: "Regulaciones ecológicas para la preservación y restauración del territorio.",
    imageUrl: "images/Criterios.png",
    baseColor: "orange",
    link: "https://siga.jalisco.gob.mx/ordenamiento/archivos/Zula/Descargas/Criterios%20de%20regulaci%C3%B3n%20ecol%C3%B3gica%20Regi%C3%B3n%20Cuenca%20R%C3%ADo%20Zula.pdf",
    target: "_blank",
  },
  {
    title: "Estrategias Ecologicas",
    description: "Proyectos de orientación para la protección del entorno natural.",
    imageUrl: "images/Estrategias.png",
    baseColor: "red",
    link: "https://siga.jalisco.gob.mx/ordenamiento/archivos/Zula/Descargas/Estrategias%20Regi%C3%B3n%20Cuenca%20R%C3%ADo%20Zula.pdf",
    target: "_blank",
  },
  {
    title: "Lineamientos",
    description: "Conservar, proteger y restaurar los recursos naturales de la región.",
    imageUrl: "images/Lineamientos.png",
    baseColor: "indigo",
    link: "https://siga.jalisco.gob.mx/ordenamiento/archivos/Zula/Descargas/Lineamientos%20ecol%C3%B3gicos%20Regi%C3%B3n%20Cuenca%20R%C3%ADo%20Zula.pdf",
    target: "_blank",
  },
  {
    title: "COMPATIBILIDADES",
    description: "Guia de compatibilidades de usos de suelo estratégicos.",
    imageUrl: "images/COMPATIBILIDADES.png",
    baseColor: "teal",
    link: "https://siga.jalisco.gob.mx/ordenamiento/archivos/Zula/Descargas/Usos%20y%20actividades%20de%20la%20Regi%C3%B3n%20Cuenca%20R%C3%ADo%20Zula.pdf",
    target: "_blank",
  },
];

const municipalities = [
  {
    id: 1,
    name: 'Arandas',
    shield: 'images/RioZula/Arandas_Escudo.jpg',
    description: 'Reconocido por su actividad agrícola y ganadera, el municipio se localiza en llanuras y ligeras elevaciones con suelos fértiles y presencia de pequeños arroyos. Sus áreas naturales permiten la implementación de prácticas de manejo sostenible del suelo',
    population: '80,609 habitantes',
    area: '949.8 km²',
    foundation: '1875',
    attractions: 'Paisajes agaveros característicos de Los Altos y visitas a tequileras locales que ofrecen experiencias agroproductivas. Se suman zonas boscosas cercanas a El Lagunazo y el Arroyo Sánchez, ideales para caminatas y observación natural.',
    color: 'from-blue-500 to-teal-600',
    administrativeRegion: 'Región Altos Sur',
    EcologicaZona: '',
    NumerodeUgas: '11 Unidades de Gestión Ambiental (UGA)',
  },
  {
    id: 2,
    name: 'Atotonilco el Alto',
    shield: 'images/RioZula/escudo_atotonilco.jpg',
    description: 'Con predominio de agricultura de riego y ganadería, el municipio cuenta con lomeríos y zonas de pastizal natural que mantienen la conectividad de ecosistemas locales. Sus recursos permiten el desarrollo de ecoturismo rural',
    population: '64,009 habitantes',
    area: '510.9 km²',
    foundation: '1932',
    attractions: 'Bosques de encino y madroño, el río Zula y manantiales rodeados de huertas frutales que han dado fama al "Jardín de Jalisco". También cuenta con balnearios como Los Chorritos y Tarethán, y miradores naturales como La Cofradía.',
    color: 'from-green-500 to-emerald-600',
    administrativeRegion: 'Cienega',
    EcologicaZona: 'Área Natural Protegida Área Estatal de Protección Hidrológica Sierra Cóndiro-Canales.',
    NumerodeUgas: '11 Unidades de Gestión Ambiental (UGA)',
  },
  {
    id: 3,
    name: 'Ayotlán',
    shield: 'images/RioZula/Ayotlan.png',
    description: 'Dedicado a la agricultura y la ganadería, presenta llanuras de suelos fértiles atravesadas por ríos temporales y canales de riego. Estas condiciones favorecen actividades de turismo rural y la implementación de programas de manejo sustentable del agua',
    population: '41,552 habitantes',
    area: '430.9 km²',
    foundation: '1884',
    attractions: 'Destaca por ríos y arroyos como el Ayo, Atargeo y Río Chico. Conserva bosques naturales en el cerro El Tigre y Ciénega de Tlaxcala. En lo cultural sobresalen la parroquia de La Soledad, el Santuario de Guadalupe y el palacio municipal.',
    color: 'from-amber-500 to-orange-600',
    administrativeRegion: 'Cienega',
    EcologicaZona: ' ',
    NumerodeUgas: '6 Unidades de Gestión Ambiental (UGA)',
  },
  {
    id: 4,
    name: 'Degollado',
    shield: 'images/RioZula/Degollado.png',
    description: 'Con actividad agrícola y ganadera, el municipio se desarrolla en llanuras y valles con presencia de humedales temporales y áreas de pastizal. Sus ecosistemas permiten la observación de especies locales y el desarrollo de prácticas de conservación',
    population: '21,226 habitantes',
    area: '426.7 km²',
    foundation: '1861',
    attractions: 'El Salto del río Lerma y el manantial semitermal Ojo de Agua destacan como atractivos naturales. Cuenta con bosques con mezquite y sabino, ideales para caminatas. En lo histórico sobrescen el templo de San Miguel Arcángel, la Parroquia de Guadalupe y el jardín principal con portales de cantera.',
    color: 'from-purple-500 to-indigo-600',
    administrativeRegion: 'Cienega',
    EcologicaZona: ' ',
    NumerodeUgas: '3 Unidades de Gestión Ambiental (UGA)',
  },
  {
    id: 5,
    name: 'Jesús María',
    shield: 'images/RioZula/jesus_maria.png',
    description: 'Sustentado en agricultura y ganadería, se localiza en zonas de lomeríos y valles con suelos fértiles y vegetación de pastizal natural. Su entorno natural favorece la aplicación de prácticas de manejo sostenible del suelo',
    population: '18,982 habitantes',
    area: '665.2 km²',
    foundation: '1875',
    attractions: 'Destacan el cerro de San Agustín, antiguo centro ceremonial con vistas panorámicas, y los manantiales como Ojos de Agua y Potrerillos. El entorno de arroyos ofrece paisajes naturales para caminatas. En lo cultural sobresale el Templo Parroquial del siglo XIX y la exhacienda de San Agustín.',
    color: 'from-green-500 to-indigo-600',
    administrativeRegion: 'Región Altos Sur',
    EcologicaZona: '',
    NumerodeUgas: '8 Unidades de Gestión Ambiental (UGA)',
  },
  {
    id: 6,
    name: 'La Barca',
    shield: 'images/RioZula/La_Barca.png',
    description: 'Destacado por la agricultura de riego y la ganadería, se ubica sobre llanuras atravesadas por el río Lerma y sus afluentes. Estas áreas favorecen la implementación de programas de conservación de agua, suelo y vegetación riparia.',
    population: '67,937 habitantes',
    area: '418.2 km²',
    foundation: '1824',
    attractions: 'Destaca el río Lerma y el arroyo El Moreño como entorno natural para paseos. Turísticamente sobresale la zona arqueológica El Portezuelo, el centro cultural La Moreña y el centro histórico con arquitectura colonial. Sus paisajes incluyen encinos, madroños y áreas boscosas en las partes altas del municipio.',
    color: 'from-blue-800 to-blue-900',
    administrativeRegion: 'Cienega',
    EcologicaZona: 'Área Natural Protegida Área Estatal de Protección Hidrológica Sierra Cóndiro-Canales.',
    NumerodeUgas: '5 Unidades de Gestión Ambiental (UGA)',
  },
  {
    id: 7,
    name: 'San Ignacio Cerro Gordo',
    shield: 'images/RioZula/San_Ignacio_Cerro_Gordo.png',
    description: 'Con tradición agrícola y ganadera, se localiza en lomeríos y valles con suelos fértiles y presencia de arroyos estacionales. Su entorno natural permite la observación de flora y fauna y el desarrollo de prácticas sostenibles de manejo del suelo y del agua.',
    population: '18,341 habitantes',
    area: '227.8 km²',
    foundation: '2003',
    attractions: 'El municipio ofrece paisajes naturales en el Cerro Gordo, ideal para miradores y senderismo, además del entorno del río El Tule y arroyos. Destacan sus templos y arquitectura religiosa, junto con la vegetación y biodiversidad conservada en los cerros cercanos.',
    color: 'from-purple-800 to-indigo-600',
    administrativeRegion: 'Región Altos Sur',
    EcologicaZona: ' ',
    NumerodeUgas: '4 Unidades de Gestión Ambiental (UGA)',
  },
  {
    id: 8,
    name: 'Tototlán',
    shield: 'images/RioZula/Tototlan.png',
    description: 'Dedicado a la agricultura y ganadería, el municipio cuenta con llanuras fértiles atravesadas por ríos temporales y canales de riego. Su territorio facilita actividades de ecoturismo rural y programas de manejo sostenible de suelos y recursos hídricos.',
    population: '23,573 habitantes',
    area: '336.6 km²',
    foundation: '1875',
    attractions: 'Tototlán ofrece atractivos naturales como el manantial La Caja de Agua, los paisajes del río Los Morales y arroyos cercanos, además de los bosques en Los Llanitos y los miradores rurales de La Isla y Yerbabuena. Destacan también el Templo de San Agustín y la Capilla de San Ignacio.',
    color: 'from-purple-800 to-indigo-600',
    administrativeRegion: 'Cienega',
    EcologicaZona: ' ',
    NumerodeUgas: '8 Unidades de Gestión Ambiental (UGA)',
  },
  {
    id: 9,
    name: 'Zapotlán del Rey',
    shield: 'images/RioZula/Zapotlan_del_Rey.png',
    description: 'Con predominio de agricultura de riego y ganadería, se localiza en valles con suelos fértiles y pequeños cuerpos de agua. Sus ecosistemas permiten la implementación de técnicas de conservación del suelo y agua, así como la observación de flora y fauna.',
    population: '19,279 habitantes',
    area: '399.9 km²',
    foundation: '1913',
    attractions: 'La zona se caracteriza por paisajes cercanos al río Santiago y varios arroyos. Entre sus puntos más atractivos están Agua Caliente del Tejocote, los Sabinos de Chila y el mirador del Cerro Grande. En el ámbito histórico destacan la Parroquia de Nuestra Señora del Socorro y antiguos cascos de haciendas.',
    color: 'from-purple-800 to-indigo-600',
    administrativeRegion: 'Cienega',
    EcologicaZona: ' ',
    NumerodeUgas: '7 Unidades de Gestión Ambiental (UGA)',
  },
];

const bitacoraItems = [
  {
    title: 'FASE DE FORMULACIÓN',
    icon: FileSignature,
    color: 'from-blue-500 to-cyan-500',
    content: [
      {
        subtitle: 'Acuerdos de Integración y Actas',
        items: [
          { text: 'Acuerdo de integración - Arandas', url: 'https://siga.jalisco.gob.mx/ordenamiento/archivos/zula/Gestion/Inicio/Acuerdo%20de%20integraci%C3%B3n%20-%20Arandas.pdf' },
          { text: 'Acuerdo de integración - Atotonilco', url: 'https://siga.jalisco.gob.mx/ordenamiento/archivos/zula/Gestion/Inicio/Acuerdo%20de%20integraci%C3%B3n%20-%20Atotonilco.pdf' },
          { text: 'Acta de aprobación - Arandas', url: 'https://siga.jalisco.gob.mx/ordenamiento/archivos/zula/Gestion/Aprobacion/Acta%20de%20aprobaci%C3%B3n%20-%20Arandas.pdf' },
          { text: 'Acta de aprobación - Atotonilco', url: 'https://siga.jalisco.gob.mx/ordenamiento/archivos/zula/Gestion/Aprobacion/Acta%20de%20aprobaci%C3%B3n%20-%20Atotonilco.pdf' },
          { text: 'Acta de aprobación - Ayotlán', url: 'https://siga.jalisco.gob.mx/ordenamiento/archivos/zula/Gestion/Aprobacion/Acta%20de%20aprobaci%C3%B3n%20-%20Ayotl%C3%A1n.pdf' }
        ]
      },
      {
        subtitle: 'Convenios',
        items: [
          { text: 'Convenio Marco de Coordinación Estado - Federación', url: 'https://siga.jalisco.gob.mx/ordenamiento/archivos/verde/Convenio/ConvenioMarcoEstado_Federacion.pdf' },
          { text: 'Convenio de Coordinación Estado-Municipios Región Cuenca del Río Zula', url: 'https://siga.jalisco.gob.mx/ordenamiento/archivos/Zula/Convenio/CONVENIO_ZULA_FIRMADO_COMPLETO_Censurado.pdf' }
        ]
      },
      {
        subtitle: 'Actas del Consejo Regional',
        items: [
          { text: 'Acta de Instalación y Primera Ordinaria', url: 'https://siga.jalisco.gob.mx/ordenamiento/archivos/Zula/Consejo/01_Acta_Instalacion_y_1er_Ord_CR_Cuenca_Rio_Zula_2022_Censurado.pdf' },
          { text: 'Primera Ordinaria 2023', url: 'https://siga.jalisco.gob.mx/ordenamiento/archivos/Zula/Consejo/03_Acta_Primera_Ordinaria_2023.pdf' },
          { text: 'Acta Segunda Sesión 2023', url: 'https://siga.jalisco.gob.mx/ordenamiento/archivos/Zula/Consejo/Acta_Segunda_Sesion_2023_Zula.pdf' }
        ]
      }
    ]
  },
  {
    title: 'FASE DE EXPEDICIÓN',
    icon: FileSpreadsheet,
    color: 'from-emerald-500 to-green-500',
    content: [
      {
        subtitle: 'Consulta Pública',
        items: [
          { text: 'Consulta Ordenamiento', url: 'https://siga.jalisco.gob.mx/ordenamientosc/zula/OrdenamientosZula.html' },
          { text: 'Respuestas a las Observaciones de la Consulta Pública', url: 'https://siga.jalisco.gob.mx/ordenamiento/archivos/Zula/Elaboracion/Respuestas_consulta_publica_Zula_2023.pdf' },
          { text: 'Documento Ajustado', url: 'https://drive.google.com/drive/folders/1TwTlmyXd5zivVoLcUoxrT8zRYkGSuxFa' },
          { text: 'Publicación Periódico del Estado Región Zula', url: 'https://siga.jalisco.gob.mx/ordenamiento/archivos/Zula/Elaboracion/newspaper240426102703.pdf' }
        ]
      }
    ]
  },
  {
    title: 'FASE DE EJECUCIÓN',
    icon: Zap,
    color: 'from-amber-500 to-orange-500',
    content: [
      {
        subtitle: 'Monitoreo',
        items: [
          { text: 'Boleta Registral', url: 'https://siga.jalisco.gob.mx/ordenamiento/archivos/zula/Gestion/Boleta_Registral.pdf' }
        ]
      }
    ]
  },
  {
    title: 'FASE DE EVALUACIÓN',
    icon: BarChart3,
    color: 'from-purple-500 to-violet-500',
    content: [
      {
        subtitle: 'Evaluaciones',
        items: []
      }
    ]
  }
];

const RioZulaMain = [
  {
    id: 0,
    title: "Publicación en Periódico Oficial",
    subtitle: "Versión completa",
    icon: <Book className="w-5 h-5 text-green-400" />,
    url: "https://siga.jalisco.gob.mx/ordenamiento/archivos/Zula/Descargas/Programa%20de%20Ordenamiento%20Ecol%C3%B3gico%20Regi%C3%B3n%20Rio%20Zula%2027%20abril%2025.pdf"
  },
  {
    id: 1,
    title: "POETR Río Zula",
    subtitle: "Programa de Ordenamiento Ecológico",
    icon: <Map className="w-5 h-5 text-emerald-400" />,
    url: "https://siga.jalisco.gob.mx/ordenamiento/archivos/Zula/Descargas/Programa%20de%20Ordenamiento%20Ecol%C3%B3gico%20Regi%C3%B3n%20Rio%20Zula.pdf"
  },
  {
    id: 2,
    title: "Plan Regional de Integración Urbana",
    subtitle: "Desarrollo Regional",
    icon: <Target className="w-5 h-5 text-rose-400" />,
    url: "https://siga.jalisco.gob.mx/ordenamiento/archivos/Zula/Descargas/Plan%20Regional%20de%20Integraci%C3%B3n%20Urbana%20Regi%C3%B3n%20Rio%20Zula.pdf"
  },
];

const ModernBanner = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleMouseMove = (e) => {
      const x = (e.clientX / window.innerWidth) * 100;
      const y = (e.clientY / window.innerHeight) * 100;
      setMousePosition({ x, y });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const stats = [
    { value: '9', label: 'Municipios', icon: MapPin },
    { value: '4,400', label: 'km² aprox.', icon: Map },
    { value: '2021', label: 'Inicio', icon: Calendar },
    { value: 'Vigente', label: 'Estado', icon: CheckCircle },
  ];

  return (
    <section className="relative h-screen min-h-[780px] overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="images/zula.jpeg"
          alt="Paisaje Cuenca Río Zula"
          className="w-full h-full object-cover"
          style={{
            transform: `translate(${mousePosition.x * 0.01}px, ${mousePosition.y * 0.01}px)`,
            transition: 'transform 0.1s ease-out'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950/80 via-slate-950/60 to-slate-950/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/20 via-transparent to-slate-950/10" />
      </div>

      <div className="relative z-10 h-full flex items-center px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto w-full">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="mb-6">
                <StatusBadge />
              </div>

              <h1 className="text-5xl md:text-7xl font-black text-white mb-6 leading-tight">
                <span className="bg-gradient-to-r from-cyan-400 to-emerald-400 bg-clip-text text-transparent">
                  Región Cuenca Río Zula
                </span>
              </h1>

              <p className="text-xl text-gray-200 mb-8 max-w-xl">
                Estrategia integral para la gestión sustentable del territorio, equilibrando el desarrollo humano con la preservación de los ecosistemas de la región.
              </p>

              <div className="flex flex-wrap gap-4">
                <button
                  onClick={() => scrollToSection('introduccion')}
                  className="px-8 py-4 bg-gradient-to-r from-cyan-500 to-emerald-500 text-white rounded-xl font-semibold hover:shadow-2xl transition-all"
                >
                  Explorar Región
                </button>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="hidden lg:grid grid-cols-2 gap-4"
            >
              {stats.map((stat, index) => {
                const Icon = stat.icon;
                return (
                  <div key={index} className="text-center">
                    <Icon className="mx-auto mb-2 text-cyan-200" size={24} />
                    <div className="text-2xl font-bold text-white mb-1">{stat.value}</div>
                    <div className="text-xs text-white/70 tracking-wide">{stat.label}</div>
                  </div>
                );
              })}
            </motion.div>
          </div>
        </div>
      </div>

      <motion.div
        animate={{ y: [0, 8, 0] }}
        transition={{ duration: 3, repeat: Infinity }}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20"
      >
        <ArrowDown className="text-cyan-400" size={32} />
      </motion.div>
    </section>
  );
};

const RioZulaPage = () => {
  const [selectedMunicipality, setSelectedMunicipality] = useState(municipalities[0]);

  return (
    <div className="environmental-theme fade-in">
      <ModernBanner />

      <section id="introduccion" className="py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-white to-gray-50 dark:from-gray-800 dark:to-gray-900 
              rounded-2xl p-8 shadow-xl border border-gray-100 dark:border-gray-700"
          >
            <div className="flex flex-col lg:flex-row items-start gap-8">
              <div className="lg:w-2/3">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-gradient-to-r from-blue-500 to-teal-500 rounded-lg">
                    <Info className="w-5 h-5 text-white" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                    Ordenamiento Ecológico Regional
                    <span className="block text-lg text-gray-600 dark:text-gray-300 mt-1">
                      Cuenca Río Zula
                    </span>
                  </h2>
                </div>

                <div className="space-y-4 text-gray-700 dark:text-gray-300">
                  <p>
                    El <strong>Gobierno del Estado de Jalisco</strong> impulsa el desarrollo sustentable 
                    en la región denominada <strong>Cuenca Río Zula</strong>, integrada por los municipios de 
                    <strong> Arandas, Atotonilco el Alto, Ayotlán, Degollado, Jesús María, La Barca, San Ignacio Cerro Gordo, Tototlán y Zapotlán del Rey</strong>.
                  </p>
                  <p>
                    Para impulsar dicho desarrollo, el día <strong>23 de noviembre de 2021</strong> el Gobierno 
                    del Estado de Jalisco y los municipios firmaron un <strong>Convenio de Coordinación</strong> que 
                    garantiza la realización de las actividades programadas.
                  </p>
                  <p>
                    Se trata de una colaboración que involucra por igual a los diferentes órdenes de gobierno: 
                    <strong> Estado y Municipios</strong>. El objeto del convenio consistió en establecer las bases 
                    para el proceso destinado a la elaboración del ordenamiento territorial.
                  </p>
                  <p>
                    Asimismo, se firmó con la Federación un <strong>Convenio Marco de Coordinación</strong> junto con <strong>SEMARNAT Y SEDATU</strong>
                    de fecha el 30 de marzo de 2020. Estos convenios establecen los fundamentos para la colaboración en el proceso de elaboración de los siguientes instrumentos de ordenamiento territorial.
                  </p>
                  <p>
                    Derivado de esto se inició con el proceso de elaboración de los instrumentos, seguido por el <strong> proceso de consulta </strong>, 
                    la cual incluyó amplia participación de los sectores que influyen en el territorio. Las fechas más relevantes sobre 
                    el proceso de elaboración se describen a continuación:
                  </p>
                </div>
              </div>

              <div className="lg:w-1/3">
                <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-100 dark:border-gray-700">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                    <ClockIcon className="w-5 h-5 text-blue-500 mr-2" />
                    Cronología Clave
                  </h3>
                  <ul className="space-y-3">
                    <li className="flex items-center text-sm">
                      <div className="w-2 h-2 bg-emerald-500 rounded-full mr-3" />
                      <span className="text-gray-700 dark:text-gray-300">
                        <strong>Inicio de proceso de formulación:</strong><br /> 23 de noviembre de 2021
                      </span>
                    </li>
                    <li className="flex items-center text-sm">
                      <div className="w-2 h-2 bg-emerald-500 rounded-full mr-3" />
                      <span className="text-gray-700 dark:text-gray-300">
                        <strong>Inicio de Consulta Pública:</strong><br />22 de julio de 2022
                      </span>
                    </li>
                    <li className="flex items-center text-sm">
                      <div className="w-2 h-2 bg-emerald-500 rounded-full mr-3" />
                      <span className="text-gray-700 dark:text-gray-300">
                        <strong>Fin de Consulta Pública:</strong><br />22 de octubre de 2022
                      </span>
                    </li>
                    <li className="flex items-center text-sm">
                      <div className="w-2 h-2 bg-blue-500 rounded-full mr-3" />
                      <span className="text-gray-700 dark:text-gray-300">
                        <strong>Sesión de validación:</strong><br />23 de marzo de 2023
                      </span>
                    </li>
                    <li className="flex items-center text-sm">
                      <div className="w-2 h-2 bg-emerald-500 rounded-full mr-3" />
                      <span className="text-gray-700 dark:text-gray-300">
                        <strong>Fecha Publicación oficial:</strong><br />27 de abril de 2024
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <section id="DocumentosE" className="py-16 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800">
        <DocumentRepository2
          items={RioZulaMain}
          onDownload={(doc) => window.open(doc.url, '_blank')}
          showFooter={false}
        />
      </section>

      <section id="Descargas" className="py-16 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800">
        <Descargas dataItems={Items} />
      </section>

      <section className="municipalities-section py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-teal-50 to-cyan-100 dark:from-gray-900 dark:to-gray-800 min-h-screen font-sans">
        <div className="container mx-auto max-w-7xl">
          <h2 className="text-4xl md:text-5xl font-extrabold text-teal-800 dark:text-teal-400 text-center mb-16 relative">
            <MapPin className="inline-block mr-3 mb-1 w-10 h-10" />
            Municipios de la Región: Río Zula
            <p className="text-lg font-normal text-gray-600 dark:text-gray-400 mt-2">
              Conoce los municipios y sus características clave.
            </p>
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            {municipalities.map((municipality) => (
              <div
                key={municipality.id}
                onClick={() => setSelectedMunicipality(municipality)}
                className={`
                  municipality-card rounded-2xl overflow-hidden shadow-xl transition-all duration-500 cursor-pointer 
                  hover:shadow-2xl hover:scale-[1.03] bg-white/90 dark:bg-gray-800/80 backdrop-blur-sm 
                  border border-gray-100 dark:border-gray-700
                  ${selectedMunicipality.id === municipality.id 
                    ? 'ring-4 ring-teal-500 scale-[1.03] shadow-teal-300/50 dark:shadow-teal-700/50' 
                    : 'hover:ring-2 hover:ring-teal-300'
                  }
                `}
              >
                <div className={`h-28 ${municipality.color} flex flex-col items-center justify-center p-3 text-white font-bold`}>
                  <img
                    src={municipality.shield}
                    alt={`Escudo de ${municipality.name}`}
                    className="h-14 w-14 object-contain mb-1 drop-shadow-md"
                    onError={(e) => { e.target.onerror = null; e.target.src = "https://placehold.co/64x64/000000/ffffff?text=E"; }}
                  />
                </div>
                <div className="p-4">
                  <h3 className="text-xl font-extrabold text-center text-gray-900 dark:text-white">{municipality.name}</h3>
                  <p className="text-xs text-center text-teal-600 dark:text-teal-400 mt-1 flex items-center justify-center">
                    Ver Detalles <ChevronRight className='w-3 h-3 ml-1'/>
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div
            key={selectedMunicipality.id}
            className="municipality-details bg-white dark:bg-gray-800 rounded-3xl shadow-2xl p-8 md:p-12 max-w-6xl mx-auto border border-teal-200 dark:border-teal-900 animate-fade-in-up"
            style={{ '--delay': '200ms' }}
          >
            <div className="flex flex-col md:flex-row items-start gap-10">
              <div className="w-full md:w-1/3 flex flex-col items-center justify-center p-4 bg-gray-50 dark:bg-gray-700 rounded-xl">
                <img
                  src={selectedMunicipality.shield}
                  alt={`Escudo de ${selectedMunicipality.name}`}
                  className="h-48 w-48 object-contain shadow-lg rounded-full p-2 bg-white dark:bg-gray-900 mb-6 border-4 border-teal-300 dark:border-teal-500"
                  onError={(e) => { e.target.onerror = null; e.target.src = "https://placehold.co/192x192/000000/ffffff?text=E"; }}
                />
                <h3 className="text-3xl font-bold mb-2 text-gray-900 dark:text-white text-center">{selectedMunicipality.name}</h3>
                <p className="text-gray-600 dark:text-gray-400 text-sm text-center italic mt-2">{selectedMunicipality.description}</p>
              </div>

              <div className="w-full md:w-2/3">
                <h4 className="text-2xl font-bold text-teal-700 dark:text-teal-400 mb-6 border-b border-gray-200 dark:border-gray-700 pb-2 flex items-center">
                  <Info className='w-5 h-5 mr-2'/> Datos Clave
                </h4>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <DetailBadge icon={Users} label="Población" value={selectedMunicipality.population} />
                  <DetailBadge icon={Map} label="Superficie" value={selectedMunicipality.area} />
                  <DetailBadge icon={Calendar} label="Municipalización" value={selectedMunicipality.foundation} />
                  <DetailBadge icon={Landmark} label="Región Administrativa" value={selectedMunicipality.administrativeRegion} />
                  <DetailBadge icon={MapPin} label="Número de UGAs" value={selectedMunicipality.NumerodeUgas} />
                  <DetailBadge icon={MountainSnow} label="Zonas de Importancia Ecológica" value={selectedMunicipality.EcologicaZona} />
                </div>

                <div className="mt-8 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <h4 className="text-xl font-bold text-gray-900 dark:text-white mb-3 flex items-center">
                    <TreePineIcon className='text-blue-500 mr-2 w-5 h-5'/> Principales Atracciones
                  </h4>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-100 dark:border-blue-800">
                    {selectedMunicipality.attractions}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="flex-1 relative bg-gray-200">
        <FullWidthVisorWithSidebar />
      </section>

      <ModernBitacoraSection items={bitacoraItems} />

      <FasesSection
        imageUrl="images/FasesOrdenamiento.png"
        altText="Fases del Ordenamiento Cuenca Río Zula"
      />
    </div>
  );
};

export default RioZulaPage;
